import React, {useMemo} from 'react';
import {ArticleDetails} from "../../core/models/interfaces/article-details.interface";
import ArticleTag from "./ui/article-tag";
import {sanitizeHTML} from "../helpers/safe-html-renderer";
import {convertLexicalJsonToHtml} from "../helpers/lexical-to-html";
import {addAnchorsToContent, generateTableOfContents} from "../helpers/generate-table-of-contents";
import SpecialistProfileImage from "../../specialists/components/specialist-profile-image";
import AppButton from "@common/components/app-button";
import {convertLexicalJsonToPlainText} from "../helpers/lexical-to-plain-text";
import {ChevronRightIcon} from "@heroicons/react/24/solid";
import {useNavigate} from "react-router-dom";

interface PublicArticleContentProps {
    article: ArticleDetails;
    onMountContent?: (el: HTMLElement) => void;
}

const PublicArticleContent: React.FC<PublicArticleContentProps> = ({article, onMountContent}) => {
    const navigate = useNavigate();

    const tableOfContents = useMemo(() => {
        if (!article?.content) return [];
        let htmlContent = article.content;
        try {
            if (article.content.startsWith('{') || article.content.startsWith('[')) {
                htmlContent = convertLexicalJsonToHtml(article.content);
            }
        } catch {
        }
        return generateTableOfContents(htmlContent);
    }, [article?.content]);

    const safeTitle: string = useMemo(() => {
        if (!article?.title) return '';
        let htmlTitle = article.title;
        try {
            if (article.title.startsWith('{') || article.title.startsWith('[')) {
                htmlTitle = convertLexicalJsonToHtml(article.title);
            }
        } catch {
        }
        return sanitizeHTML(htmlTitle);
    }, [article?.title]);

    const safeSummary: string = useMemo(() => {
        if (!article?.summary) return '';
        let htmlSummary = article.summary;
        try {
            if (article.summary.startsWith('{') || article.summary.startsWith('[')) {
                htmlSummary = convertLexicalJsonToHtml(article.summary);
            }
        } catch {
        }
        return sanitizeHTML(htmlSummary);
    }, [article?.summary]);

    const safeContent: string = useMemo(() => {
        if (!article?.content) return '';
        let htmlContent = article.content;
        try {
            if (article.content.startsWith('{') || article.content.startsWith('[')) {
                htmlContent = convertLexicalJsonToHtml(article.content);
            }
        } catch {
        }
        const contentWithAnchors = addAnchorsToContent(htmlContent);
        return sanitizeHTML(contentWithAnchors);
    }, [article?.content]);

    const goToRelatedArticle = (appId: string, stringJsonRelatedArticleTitle: string): void => {
        const plainArticleTitle: string = convertLexicalJsonToPlainText(stringJsonRelatedArticleTitle);
        navigate(`/article/${appId}/${plainArticleTitle}`);
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="inline-block align-middle text-sm text-on-surface/60 w-fit">
                {article?.tags?.map((tag, index) => (
                    <React.Fragment key={index}>
                        <ArticleTag text={tag}/>
                        {index < article?.tags.length - 1 && <span className="mx-2">·</span>}
                    </React.Fragment>
                ))}
            </div>

            <h1 className="!text-black" dangerouslySetInnerHTML={{__html: safeTitle}}/>
            <p className="!text-[#334155]" dangerouslySetInnerHTML={{__html: safeSummary}}/>

            {tableOfContents.length > 0 && (
                <ul className="space-y-2 text-link ml-5">
                    {tableOfContents.map((item, index) => (
                        <li key={index} className="flex items-center font-semibold text-base">
                            <div className="w-1 h-1 bg-link rounded-full mr-3 flex-shrink-0"></div>
                            <a
                                href={`#${item.id}`}
                                onClick={(e) => {
                                    e.preventDefault();
                                    const element = document.getElementById(item.id);
                                    if (element) {
                                        element.scrollIntoView({behavior: 'smooth', block: 'start', inline: 'nearest'});
                                    }
                                }}
                                className="cursor-pointer hover:text-link-hover transition-colors duration-200"
                            >
                                {item.title}
                            </a>
                        </li>
                    ))}
                </ul>
            )}

            <div
                className="[&>*]:!text-[#334155]"
                dangerouslySetInnerHTML={{__html: safeContent}}
                ref={(el) => {
                    el && onMountContent?.(el)
                }}
            />

            {article.exercises && article.exercises.length > 0 && (
                <section className="[&>*]:!text-[#334155] border-t border-outline-mild border-solid space-y-1 pt-8">
                    <h2 className="rich-text-h2">
                        Rekomendowane ćwiczenia
                    </h2>
                    <p className="text-on-surface/70">
                        Zestaw ćwiczeń przedstawiony poniżej został przygotowany jako praktyczne
                        rozszerzenie treści artykułu. Ich zastosowanie powinno być każdorazowo
                        dostosowane do aktualnego stanu zdrowia i zaleceń prowadzącego specjalisty.
                    </p>
                    <ul className="list-disc pl-5 space-y-1">
                        {article.exercises.map((exercise) => (
                            <li key={exercise.id}>
                                <strong>{exercise.title}</strong>
                                {exercise.description && (
                                    <span>{`: ${exercise.description}`}</span>
                                )}
                                {exercise.url && (
                                    <span>
                                        {" "}
                                        (<a
                                        href={exercise.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-link hover:text-link-hover"
                                    >
                                            Zobacz ćwiczenie
                                        </a>)
                                    </span>
                                )}
                            </li>
                        ))}
                    </ul>
                </section>
            )}

            {article?.author && (
                <section
                    className="[&>*]:!text-[#334155] border-t border-outline-mild border-solid flex flex-col gap-4 pt-6">
                    <h2 className="rich-text-h2">
                        O autorze
                    </h2>
                    <div className="flex items-center gap-4">
                        <div className="shrink-0">
                            <SpecialistProfileImage
                                specialistId={article.author.id}
                                specialistName={article.author.fullName}
                                className="w-16 h-16 !rounded-2xl"
                                classNameImg="!rounded-2xl"
                            />
                        </div>
                        <div className="flex flex-col gap-1">
                            <h3 className="font-semibold text-lg text-[#161C1D] m-0 leading-6">
                                {article.author.fullName}
                            </h3>
                            {article.author.specialization && (
                                <p className="text-[#161C1D] text-base m-0 leading-5">
                                    {article.author.specialization}
                                </p>
                            )}
                        </div>
                    </div>
                </section>

            )}

            {article?.relatedArticles && article.relatedArticles.length > 0 && (
                <section
                    className="[&>*]:!text-[#334155] border-t border-outline-mild border-solid flex flex-col gap-4 pt-6">
                    <h2 className="rich-text-h2">
                        Powiązane artykuły
                    </h2>
                    <ul className="flex flex-col gap-2 list-none p-0 m-0">
                        {article.relatedArticles.map((related) => (
                            <li key={related.id}>
                                <AppButton
                                    onClick={goToRelatedArticle.bind(this, article.appId, related.title)}
                                    classNameContentWrapper="[&>div]:w-full"
                                    className="w-full px-[14px] py-3 bg-white border border-outline-mild rounded-lg text-left hover:border-outline transition-colors duration-150 group">
                                    <div className="flex items-center justify-between w-full min-w-full">
                                        <span className="text-sm text-on-surface leading-snug truncate">
                                            {convertLexicalJsonToPlainText(related.title)}
                                        </span>
                                        <ChevronRightIcon
                                            className="print:hidden w-3.5 h-3.5 text-on-surface/30 flex-shrink-0 group-hover:text-on-surface/60 transition-colors"/>
                                    </div>
                                </AppButton>
                            </li>
                        ))}
                    </ul>
                </section>
            )}
        </div>
    );
};

export default PublicArticleContent;


